import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { store } from "@/app/store.ts";
import { RequiredDataProvider } from "@/app/RequiredDataProvider.tsx";
import Loading from "@/common/Loading";
import { AnalyticsProvider } from "@/services/analytics/analytics-context";
import * as Sentry from "@sentry/react";
import UncaughtError from "@/common/Error/UncaughtError.tsx";
import { configureAmplify } from "@/app/auth/configureAmplify.ts";
import CognitoAuthenticator from "@/app/CognitoAuthenticator.tsx";
import { AuthProvider } from "@/app/auth/AuthContext.tsx";
import MicrosoftClarityScript from "@/common/MicrosoftClarityScript";
import "./index.css";
import { IdentifiedUser } from "@/app/IdentifiedUser.tsx";
import { FeatureFlagManagerProvider } from "@/services/featureFlags/FeatureFlagManagerProvider.tsx";
import { applyInitialDarkMode } from "@/app/darkMode.ts";

// Before anything reads the session: Amplify also completes a pending Google
// redirect (?code=) the moment it is configured.
configureAmplify("app");

const AuthenticatedApp = lazy(() => import("@/app/AuthenticatedRoutes"));
const UtmScoutApp = lazy(() => import("@/app/UtmScoutRoutes"));

// Warm the route chunks while Cognito restores the session — they are
// otherwise requested only after auth and /user have resolved. Skipped for
// signed-out visitors, who may never need them.
if (
    Object.keys(localStorage).some((key) =>
        key.startsWith("CognitoIdentityServiceProvider."),
    )
) {
    import("@/app/AuthenticatedRoutes");
    import("@/app/UtmScoutRoutes");
}

if (import.meta.env.PROD) {
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        integrations: [Sentry.browserTracingIntegration()],
        // Performance Monitoring
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
        tracePropagationTargets: ["localhost"],
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });

    // Replay buffers on every session (replaysOnErrorSampleRate: 1.0), which
    // makes the recorder the most expensive part of the SDK — start it after
    // the page has loaded instead of during boot. Errors thrown before this
    // point are still captured, just without a replay attached.
    const startReplay = () => Sentry.addIntegration(Sentry.replayIntegration());
    if (document.readyState === "complete") {
        startReplay();
    } else {
        window.addEventListener("load", startReplay, { once: true });
    }
}

applyInitialDarkMode();

ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <AuthProvider>
            <AnalyticsProvider>
                {() => (
                    <Sentry.ErrorBoundary
                        fallback={<UncaughtError />}
                        // onError={(error) => {
                        //     const trackableEvent =
                        //         TrackableEventFactory.createErrorEncounteredEvent(
                        //             {
                        //                 message: ((error as { message: string })
                        //                     .message || error) as string,
                        //             },
                        //         );
                        //     analytics?.track(
                        //         trackableEvent.event,
                        //         trackableEvent.properties,
                        //     );
                        // }}
                    >
                        <Provider store={store}>
                            <CognitoAuthenticator>
                                <RequiredDataProvider>
                                    {(isLoading, user) => {
                                        // Start preloading AuthenticatedRoutes as soon as we enter RequiredDataProvider
                                        if (isLoading) {
                                            // Trigger preload
                                            import("@/app/AuthenticatedRoutes");
                                            return <Loading />;
                                        }

                                        if (!user) {
                                            // getUser failed
                                            return <UncaughtError />;
                                        }

                                        if (user.isUtmScout) {
                                            return (
                                                <Suspense
                                                    fallback={<Loading />}
                                                >
                                                    <IdentifiedUser>
                                                        <FeatureFlagManagerProvider
                                                            user={user}
                                                        >
                                                            <UtmScoutApp />
                                                        </FeatureFlagManagerProvider>
                                                    </IdentifiedUser>
                                                </Suspense>
                                            );
                                        }

                                        return (
                                            <Suspense fallback={<Loading />}>
                                                <IdentifiedUser>
                                                    <FeatureFlagManagerProvider
                                                        user={user}
                                                    >
                                                        <AuthenticatedApp />
                                                    </FeatureFlagManagerProvider>
                                                </IdentifiedUser>
                                            </Suspense>
                                        );
                                    }}
                                </RequiredDataProvider>
                            </CognitoAuthenticator>
                        </Provider>
                    </Sentry.ErrorBoundary>
                )}
            </AnalyticsProvider>
        </AuthProvider>
        <MicrosoftClarityScript />
    </React.StrictMode>,
);
